import { blurbLines } from "./steps";
import { roundedRectPath } from "./geometry";

// DOM builders for the guided tutorial (#2991). Everything is created inside
// the demo-tour controller's element so the data-action bindings attach.
// All interpolated text is our own copy from steps.js — nothing user-entered.

const SVG_NS = "http://www.w3.org/2000/svg";

// ---------------------------------------------------------------------- tray

export function buildTray(steps, { completed, progress, collapsed }) {
  const tray = document.createElement("aside");
  tray.className = `demo-tour-tray${collapsed ? " demo-tour-tray--collapsed" : ""}`;
  tray.setAttribute("aria-label", "Get started on Sindi");

  const done = steps.filter((s) => completed.has(s.id)).length;

  tray.innerHTML = `
    <button type="button" class="demo-tour-tray__header" aria-expanded="${!collapsed}"
            data-action="click->demo-tour#toggleTray">
      <h2 class="demo-tour-tray__title">Get started on Sindi</h2>
      <span class="demo-tour-tray__count">${done}/${steps.length}</span>
      <i class="demo-tour-tray__chevron fa-solid fa-chevron-${collapsed ? "up" : "down"}" aria-hidden="true"></i>
    </button>
    <div class="demo-tour-tray__body"><div><div class="demo-tour-tray__rows"></div></div></div>
  `;

  const rows = tray.querySelector(".demo-tour-tray__rows");
  steps.forEach((step, i) => {
    rows.appendChild(
      buildRow(step, i + 1, completed.has(step.id), progress[step.id] || 0)
    );
  });

  return tray;
}

function buildRow(step, index, done, at) {
  const partway = at > 0;
  const action = partway ? "Continue" : done ? "Replay" : "Start";
  // Restarting only means something once there is something to throw away.
  const restartable = partway || done;

  const row = document.createElement("div");
  row.className = "demo-tour-row";

  const blurb = blurbLines(step.blurb)
    .map((line) => `<span>${line}</span>`)
    .join("");
  const meta = partway ? `${at + 1} of ${step.beats.length}` : `${step.minutes} min`;

  row.innerHTML = `
    <button type="button" class="demo-tour-row__main" aria-label="${action}: ${step.label}"
            data-action="click->demo-tour#startStep" data-step-id="${step.id}">
      ${rowMarker(done, index, at, step.beats.length)}
      <span class="demo-tour-row__content">
        <span class="demo-tour-row__top">
          <span class="demo-tour-row__label">${step.label}</span>
          <span class="demo-tour-row__meta">${meta}</span>
        </span>
        <span class="demo-tour-row__blurb">${blurb}</span>
      </span>
    </button>
    <button type="button" data-step-id="${step.id}"
            ${restartable
              ? `class="demo-tour-row__action demo-tour-row__action--restart" aria-label="Start ${step.label} again" title="Start again" data-action="click->demo-tour#restartStep"`
              : `class="demo-tour-row__action" aria-hidden="true" tabindex="-1" title="${action}" data-action="click->demo-tour#startStep"`}>
      <i class="fa-solid ${restartable ? "fa-rotate-left" : "fa-arrow-right"}" aria-hidden="true"></i>
    </button>
  `;

  return row;
}

// The number is already a circle, so the circle carries how far in they got.
function rowMarker(done, index, at, of) {
  if (done) {
    return '<i class="demo-tour-row__check fa-solid fa-circle-check" aria-hidden="true"></i>';
  }

  const fraction = of > 0 ? Math.min(at / of, 1) : 0;
  const circumference = 2 * Math.PI * 9;
  const progressCircle = fraction > 0
    ? `<circle cx="10" cy="10" r="9" fill="none" stroke="#F1616E" stroke-width="2"
               stroke-linecap="round" stroke-dasharray="${circumference}"
               stroke-dashoffset="${circumference * (1 - fraction)}"/>`
    : "";

  return `
    <span class="demo-tour-row__marker${fraction > 0 ? " demo-tour-row__marker--started" : ""}">
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="9" fill="none" stroke="rgba(0,0,0,0.2)" stroke-width="1"/>
        ${progressCircle}
      </svg>
      <span>${index}</span>
    </span>
  `;
}

/**
 * The collapsed bar a finished track leaves behind: just "Replay tutorial"
 * with the action and a dismiss. Dismissing hides it for the session; it
 * comes back next visit.
 */
export function buildReplayPill() {
  const tray = document.createElement("aside");
  tray.className = "demo-tour-tray";
  tray.setAttribute("aria-label", "Replay the tutorial");
  tray.innerHTML = `
    <div class="demo-tour-tray__header demo-tour-tray__header--static">
      <button type="button" class="demo-tour-replay-label"
              data-action="click->demo-tour#replayTutorial">
        <span>Replay Tutorials</span>
        <i class="fa-solid fa-rotate-left" aria-hidden="true"></i>
      </button>
      <button type="button" class="demo-tour-replay-close" aria-label="Dismiss"
              data-action="click->demo-tour#dismissReplayPill">
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
    </div>
  `;
  return tray;
}

// ----------------------------------------------------------- spotlight layers

// Far larger than any viewport, so the wash needs no measuring and nothing to
// recompute on resize.
const OUTER_PATH = "M-9999 -9999H19999V19999H-9999Z";

/**
 * The wash over everything the current beat is not talking about. White
 * rather than black: the app is white, and darkening it turns a screen the
 * user knows into one they have to re-read.
 */
export function buildScrim() {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "demo-tour-scrim");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("fill-rule", "evenodd");
  path.setAttribute("fill", "#ffffff");
  path.setAttribute("fill-opacity", "0.72");
  path.setAttribute("d", OUTER_PATH);
  svg.appendChild(path);
  return { svg, path };
}

export function updateScrim(path, rect) {
  path.setAttribute("d", rect ? `${OUTER_PATH} ${roundedRectPath(rect)}` : OUTER_PATH);
}

/**
 * A hole in the paint is a hole in hit-testing too, so the click blocking is
 * its own layer — otherwise the one thing being talked about would be the
 * one thing still clickable.
 */
export function buildBlocker() {
  const blocker = document.createElement("div");
  blocker.className = "demo-tour-blocker";
  blocker.setAttribute("aria-hidden", "true");
  blocker.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
  });
  return blocker;
}

/** The line drawn around whatever the current beat is talking about. */
export function buildRing(rect, fading = false) {
  const box = document.createElement("div");
  box.className = `demo-tour-ring-box${fading ? " demo-tour-ring-box--fading" : ""}`;
  box.setAttribute("aria-hidden", "true");
  positionRing(box, rect);
  box.innerHTML = '<div class="demo-tour-ring"></div>';
  return box;
}

export function positionRing(box, rect) {
  box.style.left = `${rect.x}px`;
  box.style.top = `${rect.y}px`;
  box.style.width = `${rect.width}px`;
  box.style.height = `${rect.height}px`;
}

// ---------------------------------------------------------------------- card

/**
 * The callout card. One instance lives for the whole module and moves
 * between beats rather than remounting, so position and height transition.
 * Height can't animate from `auto`, so the content is measured and the box
 * given that height explicitly.
 */
export function buildCard() {
  const card = document.createElement("div");
  card.className = "demo-tour-card";
  card.innerHTML = `
    <div class="demo-tour-card__wash" aria-hidden="true"></div>
    <div class="demo-tour-card__content"></div>
  `;
  const content = card.querySelector(".demo-tour-card__content");

  const observer = new ResizeObserver(() => {
    card.style.height = `${content.getBoundingClientRect().height}px`;
  });
  observer.observe(content);
  card._resizeObserver = observer;

  return { card, content };
}

export function renderCardContent(content, beat, index, count) {
  const body = beat.body.map((p) => `<p>${p}</p>`).join("");
  const pips = Array.from({ length: count }, (_, i) => {
    const cls = i === index ? "demo-tour-pips--current" : i < index ? "demo-tour-pips--done" : "";
    return `<span class="${cls}"></span>`;
  }).join("");

  content.innerHTML = `
    <div class="demo-tour-card__head">
      <p class="demo-tour-card__title">${beat.title}</p>
      <button type="button" class="demo-tour-card__close" aria-label="Close tutorial"
              data-action="click->demo-tour#exitModule">
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
    </div>
    <div class="demo-tour-card__body">${body}</div>
    <div class="demo-tour-card__footer">
      <div class="demo-tour-card__side">
        <button type="button" class="demo-tour-card__back" ${index === 0 ? "disabled" : ""}
                data-action="click->demo-tour#prevBeat">Previous</button>
      </div>
      <div class="demo-tour-pips">${pips}</div>
      <div class="demo-tour-card__side">
        <button type="button" class="demo-tour-card__next"
                data-action="click->demo-tour#nextBeat">${beat.cta}</button>
      </div>
    </div>
  `;
}

export function positionCard(card, placement) {
  card.style.left = `${placement.left}px`;
  card.style.top = `${placement.top}px`;
  // Always an explicit translate, never none: this is the property that
  // carries the flip from below an anchor to above it.
  card.style.transform = `translateY(${placement.above ? "-100%" : "0%"})`;
}

// --------------------------------------------------------------- celebration

export function buildCelebration() {
  const overlay = document.createElement("div");
  overlay.className = "demo-tour-celebration";
  // Clicking the backdrop dismisses — celebration only; the module callouts
  // keep their blocker semantics.
  overlay.setAttribute("data-action", "click->demo-tour#celebrationBackdropClick");
  overlay.innerHTML = `
    <div class="demo-tour-celebration__card">
      <button type="button" class="demo-tour-celebration__close" aria-label="Close"
              data-action="click->demo-tour#dismissCelebration">
        <i class="fa-solid fa-xmark" aria-hidden="true"></i>
      </button>
      <h2 class="demo-tour-celebration__title">You know your way around now!</h2>
      <p class="demo-tour-celebration__text">That is the whole walkthrough. Everything you just did works the same way on real referrals.</p>
    </div>
  `;
  return overlay;
}
